import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import LibraryMusicOutlined from "@mui/icons-material/LibraryMusicOutlined";
import { GlobalContext } from "../../../context/GlobalContext";
import { mediaSrc } from "../../../utils/mediaSrc";

const FETCH_LIMIT = 100;

function flattenSongTracks(songs) {
  const rows = [];
  for (const song of songs || []) {
    if (song.status !== "completed") continue;
    const tracks = Array.isArray(song.tracks) ? song.tracks : [];
    tracks.forEach((tr, index) => {
      const stored = tr?.stored;
      if (!stored) return;
      rows.push({
        key: `${song.id}-${index}`,
        jobId: song.id,
        trackIndex: index,
        songTitle: song.title || `Song #${song.id}`,
        trackTitle: tr.title || `Track ${index + 1}`,
        audioUrl: stored,
      });
    });
  }
  return rows;
}

function MusicPickRow({ row, selected, onSelect, lang }) {
  const src = mediaSrc(row.audioUrl);
  return (
    <Box
      onClick={() => onSelect(row)}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected ? (t) => alpha(t.palette.primary.main, 0.06) : "background.paper",
        cursor: "pointer",
        "&:hover": { borderColor: selected ? "primary.main" : "primary.light" },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} mb={0.75}>
        <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: 1 }}>
          {row.songTitle}
        </Typography>
        {selected ? (
          <Chip size="small" color="primary" label={lang?.videoStudioMusicSelected || "Selected"} />
        ) : null}
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        {row.trackTitle} · #{row.jobId}
      </Typography>
      {src ? (
        <Box
          component="audio"
          controls
          src={src}
          onClick={(e) => e.stopPropagation()}
          sx={{ width: "100%", height: 36 }}
        />
      ) : null}
    </Box>
  );
}

/**
 * @param {{
 *   open: boolean;
 *   onClose: () => void;
 *   selectedKey?: string;
 *   onConfirm: (row: object) => void;
 *   lang?: object;
 * }} props
 */
export default function PickMusicLibraryModal({ open, onClose, selectedKey, onConfirm, lang }) {
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const [loading, setLoading] = React.useState(false);
  const [tracks, setTracks] = React.useState([]);
  const [pick, setPick] = React.useState(null);
  const [search, setSearch] = React.useState("");

  const loadTracks = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await hitAxios({
        path: `/api/music/songs/jobs?page=1&limit=${FETCH_LIMIT}`,
        post: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (res?.data?.success && Array.isArray(res.data.data?.jobs)) {
        setTracks(flattenSongTracks(res.data.data.jobs));
      } else {
        setTracks([]);
      }
    } catch (err) {
      showSnack?.(err?.message || lang?.videoStudioMusicLibraryLoadFailed || "Could not load music.", false);
      setTracks([]);
    } finally {
      setLoading(false);
    }
  }, [hitAxios, lang, showSnack]);

  React.useEffect(() => {
    if (!open) return;
    loadTracks();
    setSearch("");
  }, [open, loadTracks]);

  React.useEffect(() => {
    if (!open) return;
    setPick(tracks.find((t) => t.key === selectedKey) || null);
  }, [open, selectedKey, tracks]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tracks;
    return tracks.filter(
      (t) =>
        String(t.songTitle || "").toLowerCase().includes(q) ||
        String(t.trackTitle || "").toLowerCase().includes(q),
    );
  }, [tracks, search]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
        <LibraryMusicOutlined color="primary" />
        <Box sx={{ flex: 1 }}>{lang?.videoStudioMusicLibraryTitle || "Your generated music"}</Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {lang?.videoStudioMusicLibraryHint ||
              "Pick a completed track from Music Studio. Preview before selecting."}
          </Typography>
          <TextField
            size="small"
            fullWidth
            label={lang?.search || "Search"}
            placeholder={lang?.videoStudioMusicLibrarySearchPh || "Search by title…"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : null}
          {!loading && filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
              {lang?.videoStudioMusicLibraryEmpty ||
                "No completed songs yet. Generate music in Music Studio first."}
            </Typography>
          ) : null}
          <Stack spacing={1} sx={{ maxHeight: 400, overflow: "auto" }}>
            {filtered.map((row) => (
              <MusicPickRow
                key={row.key}
                row={row}
                selected={pick?.key === row.key}
                onSelect={setPick}
                lang={lang}
              />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{lang?.cancel || "Cancel"}</Button>
        <Button
          variant="contained"
          disabled={!pick}
          onClick={() => {
            if (pick) onConfirm(pick);
            onClose();
          }}
        >
          {lang?.videoStudioMusicUseSelected || "Use track"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
